// ABOUTME: Sidebar navigation component for hypothesis categories
// ABOUTME: Uses React Router for navigation instead of callbacks

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CategoryInfo, HypothesisMetadata } from '@/types/analysis';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarProps {
  categories: CategoryInfo[];
  isLoading?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ categories, isLoading }) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleSelectHypothesis = (category: string, hypothesis: HypothesisMetadata) => {
    navigate(`/dashboard/hypothesis/${category}/${hypothesis.id}`);
  };

  const handleShowOverview = () => {
    navigate('/dashboard');
  };

  // Determine if we're on overview page
  const isOverviewPage = location.pathname === '/dashboard';
  const isInterviewsPage = location.pathname.startsWith('/dashboard/interviews');
  const isAnalysisPage = location.pathname === '/dashboard/analysis';
  const isPersonasPage = location.pathname === '/dashboard/personas';
  const isGlossaryPage = location.pathname === '/dashboard/glossary';
  const isChatPage = location.pathname.includes('/dashboard/chat');
  const isUserManagementPage = location.pathname === '/admin/users';

  const isAdmin = user?.role === 'admin';
  const canAccessChat = user?.role === 'admin' || user?.role === 'exco' || user?.role === 'leads';

  if (isLoading) {
    return (
      <div className="w-80 bg-gray-50 border-r border-gray-200 overflow-y-auto">
        <div className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-8 bg-gray-300 rounded"></div>
            <div className="h-8 bg-gray-300 rounded"></div>
            <div className="h-8 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-gray-50 border-r border-gray-200 overflow-y-auto">
      <div className="p-4">
        {/* Main Navigation */}
        <div className="mb-6 space-y-1">
          <button
            onClick={handleShowOverview}
            className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-base ${
              isOverviewPage ? 'bg-alx-green/10 text-alx-blue font-medium' : 'hover:bg-gray-100'
            }`}
          >
            📊 Overview Dashboard
          </button>

          <button
            onClick={() => navigate('/dashboard/interviews')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-base ${
              isInterviewsPage ? 'bg-alx-green/10 text-alx-blue font-medium' : 'hover:bg-gray-100'
            }`}
          >
            🎙 Interviews
          </button>

          <button
            onClick={() => navigate('/dashboard/personas')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-base ${
              isPersonasPage ? 'bg-alx-green/10 text-alx-blue font-medium' : 'hover:bg-gray-100'
            }`}
          >
            👥 Functional Groups
          </button>

          <button
            onClick={() => navigate('/dashboard/glossary')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-base ${
              isGlossaryPage ? 'bg-alx-green/10 text-alx-blue font-medium' : 'hover:bg-gray-100'
            }`}
          >
            📖 Research Glossary
          </button>

          {canAccessChat && (
            <button
              onClick={() => navigate('/dashboard/chat')}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-base ${
                isChatPage ? 'bg-alx-green/10 text-alx-blue font-medium' : 'hover:bg-gray-100'
              }`}
            >
              💬 Chat Assistant
            </button>
          )}

          {isAdmin && (
            <button
              onClick={() => navigate('/dashboard/analysis')}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-base ${
                isAnalysisPage ? 'bg-alx-green/10 text-alx-blue font-medium' : 'hover:bg-gray-100'
              }`}
            >
              🔬 Hypothesis Analysis
            </button>
          )}

          {isAdmin && (
            <button
              onClick={() => navigate('/admin/users')}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-base ${
                isUserManagementPage ? 'bg-alx-green/10 text-alx-blue font-medium' : 'hover:bg-gray-100'
              }`}
            >
              👤 User Management
            </button>
          )}
        </div>

        {/* Separator and Hypothesis Analyses header */}
        <div className="border-t border-gray-300 pt-4 mb-3">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 px-2">
            Hypotheses
          </h3>
        </div>

        {categories.map((category) => (
          <div key={category.id} className="mb-2">
            <button
              onClick={() => toggleCategory(category.id)}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-between"
            >
              <span className="font-medium text-gray-700">
                🧭 {category.displayName} ({category.count})
              </span>
              <span className="text-gray-400">
                {expandedCategories.has(category.id) ? '▼' : '▶'}
              </span>
            </button>

            {expandedCategories.has(category.id) && (
              <div className="ml-4 mt-1">
                {category.hypotheses.map((hypothesis) => {
                  const isSelected = location.pathname.includes(`/hypothesis/${category.id}/${hypothesis.id}`);
                  return (
                    <button
                      key={hypothesis.id}
                      onClick={() => handleSelectHypothesis(category.id, hypothesis)}
                      className={`w-full text-left px-3 py-1.5 text-sm rounded transition-colors ${
                        isSelected
                          ? 'bg-alx-green/10 text-alx-blue border-l-2 border-alx-green font-medium'
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >
                      {hypothesis.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {/* Feedback Section */}
        <div className="mt-6 pt-4 border-t border-gray-300">
          <a
            href="https://docs.google.com/forms/d/1dRnHJanUP2nD6URGermzlO9GoReNf9h0MccCAzSsDAg/edit"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full text-left px-3 py-2 rounded-lg transition-colors text-sm flex items-center gap-2 hover:bg-gray-100 text-gray-700"
          >
            <span>💬</span>
            <span>Give Feedback</span>
          </a>
          <p className="text-xs text-gray-500 px-3 mt-1">
            Report issues or suggestions
          </p>
        </div>
      </div>
    </div>
  );
};
